import {
  API_ROUTES,
  type CreatePetRequest,
  type PetDto,
  type WorldDto,
  apiErrorSchema,
  petSchema,
  worldSchema,
} from '@tama/api-contract';
import type { ZodType } from 'zod';
import { API_URL } from '../config';
import { getAccessToken } from './session';
import { supabase } from './supabase';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Internal: prevents an infinite refresh loop. */
  retryOnUnauthorized?: boolean;
}

/**
 * One authenticated round trip, including the single token refresh a 401 earns.
 *
 * Shared by the parsing and no-content wrappers below so that auth, retry and
 * error translation cannot drift between them.
 */
async function authenticatedFetch(
  path: string,
  { method = 'GET', body, retryOnUnauthorized = true }: RequestOptions = {},
): Promise<Response> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new ApiError(401, 'NOT_SIGNED_IN', 'Sign in to look after your pet.');
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 401 && retryOnUnauthorized) {
    // supabase-js refreshes proactively, so a 401 here means the cached token
    // really is dead (rotated signing key, revoked session). Force one refresh
    // and retry; if that also fails the error surfaces and the UI signs out.
    const { error } = await supabase.auth.refreshSession();
    if (error) throw await toApiError(response);
    return authenticatedFetch(path, { method, body, retryOnUnauthorized: false });
  }

  if (!response.ok) throw await toApiError(response);

  return response;
}

/**
 * The single place the client talks to the server.
 *
 * Every response is parsed with the shared schema, so a contract mismatch shows
 * up here as a clear error rather than as `undefined` deep inside a component.
 */
async function apiRequest<T>(
  path: string,
  schema: ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const response = await authenticatedFetch(path, options);
  return schema.parse(await response.json());
}

/** For endpoints that answer 204 — there is no body to parse or validate. */
async function apiRequestNoContent(path: string, options: RequestOptions = {}): Promise<void> {
  await authenticatedFetch(path, options);
}

async function toApiError(response: Response): Promise<ApiError> {
  const raw = await response.text().catch(() => '');
  try {
    const parsed = apiErrorSchema.parse(JSON.parse(raw));
    return new ApiError(parsed.statusCode, parsed.code, parsed.message);
  } catch {
    // Not the contract shape: an nginx error page, a proxy timeout, a gateway
    // rejection before the app was ever reached. Pass the body through rather
    // than reduce it to a status code nobody can act on.
    const detail = raw.trim().replace(/\s+/g, ' ').slice(0, 300);
    return new ApiError(
      response.status,
      'UNKNOWN',
      detail
        ? `Request failed (HTTP ${response.status}): ${detail}`
        : `Request failed (HTTP ${response.status}).`,
    );
  }
}

export const api = {
  getPet: (): Promise<PetDto> => apiRequest(API_ROUTES.myPet, petSchema),

  createPet: (input: CreatePetRequest): Promise<PetDto> =>
    apiRequest(API_ROUTES.createPet, petSchema, { method: 'POST', body: input }),

  deletePet: (): Promise<void> => apiRequestNoContent(API_ROUTES.myPet, { method: 'DELETE' }),

  getWorld: (): Promise<WorldDto> => apiRequest(API_ROUTES.myWorld, worldSchema),

  // The save and the reset both answer with the stored world, so both parse it
  // — the server may have repaired what it was sent.
  saveWorld: (world: WorldDto): Promise<WorldDto> =>
    apiRequest(API_ROUTES.myWorld, worldSchema, { method: 'PUT', body: world }),

  resetWorld: (): Promise<WorldDto> =>
    apiRequest(API_ROUTES.myWorld, worldSchema, { method: 'DELETE' }),
};
